import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import { Card, Spin, Typography, Button } from "antd";
import {
  DollarOutlined,
  CreditCardOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { getRentPayment } from "../lib/firebase/paymentModel";
import Logo from "../components/general/Logo";

const { Title, Text } = Typography;

const PageWrap = styled.div`
  min-height: 100vh;
  background: var(--color-background, #f5f5f5);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledCard = styled(Card)`
  max-width: 420px;
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  .ant-card-body {
    padding: 1.5rem;
  }
`;

const BalanceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f0;
`;

const QuickActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  margin-top: 1rem;
`;

const QuickActionButton = styled(Button)`
  width: 100%;
  height: 48px;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &.ant-btn-primary {
    background: var(--color-primary, #2c5f2d);
    border-color: var(--color-primary, #2c5f2d);
  }
  &.ant-btn-default {
    border-color: #d9d9d9;
    color: #262626;
  }
`;

const PayButton = styled(Button)`
  width: 100%;
  height: 48px;
  font-size: 1rem;
  background: var(--color-primary, #2c5f2d);
  border-color: var(--color-primary, #2c5f2d);
  &:hover {
    background: var(--color-primary-hover, #234a24);
    border-color: var(--color-primary-hover, #234a24);
  }
`;

const LogoWrap = styled.div`
  margin-bottom: 1.5rem;
  text-align: center;
`;

/**
 * Tenant View: persistent route for tenants to see current balance and pay.
 * Uses payment id in URL; "Pay Now" links to the existing Stripe checkout flow (/pay-rent/:id).
 */
const TenantView = () => {
  const { id } = useParams();
  const {
    data: result,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["tenant-view-payment", id],
    queryFn: () => getRentPayment(id),
    enabled: !!id,
  });

  const payment = result?.success ? result.data || result.payment : null;
  const isPaid =
    payment?.status === "paid" ||
    payment?.status === "processing" ||
    payment?.status === "pending_verification" ||
    payment?.status === "PENDING_VERIFICATION";
  const amountDue = payment?.amount != null ? Number(payment.amount) : 0;

  if (isLoading) {
    return (
      <PageWrap>
        <Spin size="large" tip="Loading…" />
      </PageWrap>
    );
  }

  if (error || !payment) {
    return (
      <PageWrap>
        <StyledCard>
          <Title level={5}>Unable to load payment</Title>
          <Text type="secondary">
            This link may be invalid or expired. Contact your landlord for a new
            payment link.
          </Text>
        </StyledCard>
      </PageWrap>
    );
  }

  return (
    <PageWrap>
      <StyledCard>
        <LogoWrap>
          <Logo />
        </LogoWrap>
        <Title level={4} style={{ marginBottom: 0, textAlign: "center" }}>
          Your rent
        </Title>
        <BalanceRow>
          <span>
            <DollarOutlined style={{ marginRight: 8, color: "#666" }} />
            <Text strong>Current balance</Text>
          </span>
          <Text strong style={{ fontSize: "1.25rem" }}>
            $
            {amountDue.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
        </BalanceRow>
        <QuickActions>
          {payment?.tenantId && (
            <>
              <Text
                type="secondary"
                style={{ fontSize: "0.75rem", marginBottom: 4 }}
              >
                Financials
              </Text>
              <QuickActionButton
                type="default"
                size="large"
                icon={<DollarOutlined />}
                as={Link}
                to={`/tenant-ledger/${payment.tenantId}`}
              >
                View Ledger
              </QuickActionButton>
            </>
          )}
          {isPaid ? (
            <Text
              type="secondary"
              style={{ display: "block", textAlign: "center", marginBottom: 8 }}
            >
              This payment is already completed or in progress.
            </Text>
          ) : (
            <Link to={`/pay-rent/${id}`} style={{ display: "block" }}>
              <PayButton
                type="primary"
                size="large"
                icon={<CreditCardOutlined />}
              >
                Pay Rent
              </PayButton>
            </Link>
          )}
          <QuickActionButton
            type="default"
            size="large"
            icon={<ToolOutlined />}
            href="/contact"
            target="_self"
            as="a"
          >
            Request Repair
          </QuickActionButton>
        </QuickActions>
        <Text
          type="secondary"
          style={{
            fontSize: "0.75rem",
            marginTop: 12,
            textAlign: "center",
            display: "block",
          }}
        >
          To request a repair via chat, use the tenant chat link your landlord
          sent you. Use <strong>?intent=request_repair</strong> to skip to
          repair requests.
        </Text>
      </StyledCard>
    </PageWrap>
  );
};

export default TenantView;
